import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  dashApi, type AgendaResult, type Cadence, type EffectivenessResult, type PortfolioSummary,
} from "@/lib/dashApi";
import { EmptyState, ErrorState, LoadingState, PageHeader, Panel, Stat } from "@/components/dash/ui";

const TABS = [
  { key: "portfolio", label: "Portfolio" },
  { key: "agenda", label: "Review Agenda" },
  { key: "effectiveness", label: "Effectiveness" },
  { key: "cadences", label: "Cadences" },
] as const;
type TabKey = (typeof TABS)[number]["key"];

export default function DecisionPortfolioPage() {
  const [tab, setTab] = useState<TabKey>("portfolio");
  return (
    <div className="space-y-6">
      <PageHeader
        title="Decision Portfolio & Management Effectiveness"
        description="Governance oversight of the whole decision portfolio — which decisions need review, are delayed/blocked/unowned, lack follow-through, or carry unresolved risks. Aggregate scenario alignment is shown only where the sample is adequate. This is governance and learning: it never ranks individuals and never claims a decision caused a business result. IRONDash-only; no source writes."
      />
      <div className="flex flex-wrap gap-2 text-sm">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`rounded px-3 py-1 ${tab === t.key ? "bg-sky-500/20 text-sky-200" : "bg-slate-800 text-slate-400"}`}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === "portfolio" && <PortfolioTab />}
      {tab === "agenda" && <AgendaTab />}
      {tab === "effectiveness" && <EffectivenessTab />}
      {tab === "cadences" && <CadencesTab />}
    </div>
  );
}

const COUNT_LABELS: [string, string][] = [
  ["total_active", "Active decisions"], ["proposed", "Awaiting approval"], ["review_overdue", "Overdue reviews"],
  ["awaiting_review", "Awaiting review"], ["approved_no_action", "Approved, no action"],
  ["blocked_action", "Blocked-action decisions"], ["overdue_action", "Overdue-action decisions"],
  ["unassigned_action", "Unassigned-action decisions"], ["stale_scenario", "Stale scenario input"],
  ["unsupported_scope", "Unsupported scope"], ["reopened_risk", "Reopened-action decisions"],
  ["without_owner", "Without owner"], ["without_action", "Without action"],
  ["accepted_risks_expiring", "Accepted risks expiring"], ["superseded", "Superseded"], ["cancelled", "Cancelled"],
];

function sev(n: number, warn = 1): string {
  return n >= warn ? "text-amber-300" : "text-slate-300";
}

function PortfolioTab() {
  const q = useQuery({ queryKey: ["portfolio-summary"], queryFn: () => dashApi.portfolioSummary() });
  if (q.isLoading) return <LoadingState />;
  if (q.isError) return <ErrorState error={q.error} />;
  const s = q.data as PortfolioSummary;
  const c = s.counts;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-4">
        {COUNT_LABELS.map(([k, label]) => (
          <div key={k} className="rounded-lg border border-slate-800 bg-slate-900/40 px-3 py-2">
            <div className={`text-xl font-semibold ${sev(c[k] ?? 0)}`}>{c[k] ?? 0}</div>
            <div className="text-[11px] text-slate-500">{label}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Stat label="Action coverage" value={s.action_coverage_pct != null ? `${s.action_coverage_pct}%` : "—"}
          sub="active decisions with a live action" />
        <Stat label="Review on-time rate"
          value={s.metrics.review_on_time_rate_pct != null ? `${s.metrics.review_on_time_rate_pct}%` : "withheld"}
          sub={s.metrics.note ?? `${s.metrics.reviewed_count} reviewed`} />
        <Stat label="Governance completeness"
          value={`${s.governance_completeness.owner ?? 0}/${c.total_active}`} sub="have an owner" />
      </div>

      <SectionList title="Overdue reviews" items={s.review_overdue} empty="No overdue reviews." />
      <SectionList title="Approved with no linked action" items={s.approved_no_action} empty="All approved decisions have an action." />
      <SectionList title="Awaiting review" items={s.awaiting_review} empty="Nothing awaiting review." />
      <SectionList title="Blocked-action decisions" items={s.blocked_action} empty="No blocked-action decisions." />

      {s.accepted_risks_expiring.length > 0 && (
        <Panel title="Accepted risks nearing expiry / expired" subtitle="Expired accepted risks resurface — re-confirm or withdraw.">
          <ul className="space-y-1 text-xs">
            {s.accepted_risks_expiring.map((r) => (
              <li key={r.id} className="flex flex-wrap items-center gap-2">
                <span className={r.effective_status === "expired" ? "text-rose-300" : "text-amber-300"}>{r.effective_status}</span>
                <span className="text-slate-200">{r.title}</span>
                <span className="text-slate-500">(decision #{r.decision_id}, {r.severity}{r.expiry_date ? `, expires ${r.expiry_date}` : ""})</span>
              </li>
            ))}
          </ul>
        </Panel>
      )}
      <p className="text-[11px] text-slate-600">{s.no_causation}</p>
    </div>
  );
}

function SectionList({ title, items, empty }: { title: string; items: { id: number; title: string; status: string; owner: string | null; target_review_date: string | null }[]; empty: string }) {
  return (
    <Panel title={`${title} (${items.length})`}>
      {items.length === 0 ? <EmptyState>{empty}</EmptyState> : (
        <ul className="space-y-1 text-xs">
          {items.map((d) => (
            <li key={d.id} className="flex flex-wrap items-center gap-2">
              <span className="rounded bg-slate-800 px-1.5 py-0.5 text-[10px] text-slate-400">{d.status}</span>
              <span className="flex-1 truncate text-slate-200">{d.title}</span>
              {d.owner ? <span className="text-slate-500">👤 {d.owner}</span> : <span className="text-amber-400">unowned</span>}
              {d.target_review_date && <span className="text-slate-500">review {d.target_review_date}</span>}
              <a href="/decision-records" className="text-sky-400">open</a>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

const SEV_TONE: Record<string, string> = { critical: "border-rose-500/40 bg-rose-500/5", warning: "border-amber-500/30 bg-amber-500/5", info: "border-slate-800 bg-slate-900/40" };

function AgendaTab() {
  const q = useQuery({ queryKey: ["portfolio-agenda"], queryFn: () => dashApi.portfolioAgenda() });
  if (q.isLoading) return <LoadingState />;
  if (q.isError) return <ErrorState error={q.error} />;
  const a = q.data as AgendaResult;
  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3 text-[11px] text-slate-500">
        {a.no_recommendation} {a.no_causation}
      </div>
      {a.discussion_order.length > 0 && (
        <Panel title="Recommended discussion order" subtitle="Deterministic by severity & timing — recommends no specific decision.">
          <ol className="space-y-1 text-xs">
            {a.discussion_order.map((it, i) => (
              <li key={`${it.decision_id}-${i}`} className="flex flex-wrap items-center gap-2">
                <span className="text-slate-600">{i + 1}.</span>
                <span className={it.severity === "critical" ? "text-rose-300" : it.severity === "warning" ? "text-amber-300" : "text-slate-300"}>●</span>
                <span className="text-slate-200">{it.title}</span>
                <span className="text-slate-500">— {it.why}</span>
              </li>
            ))}
          </ol>
        </Panel>
      )}
      {a.sections.filter((s) => !s.empty).map((s) => (
        <Panel key={s.key} title={`${s.title} (${s.items.length})`}>
          <ul className="space-y-2 text-xs">
            {s.items.map((it, i) => (
              <li key={`${it.decision_id}-${i}`} className={`rounded border p-2 ${SEV_TONE[it.severity] ?? SEV_TONE.info}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-slate-200">#{it.decision_id} {it.title}</span>
                  <span className="text-[10px] text-slate-500">{it.category} · {it.status}</span>
                  {it.review_due && <span className="text-[10px] text-slate-500">review {it.review_due}</span>}
                  <a href={it.link} className="ml-auto text-sky-400">open</a>
                </div>
                <div className="mt-0.5 text-slate-400">{it.why}</div>
                {it.caveat && <div className="mt-0.5 text-[10px] text-slate-600">{it.caveat}</div>}
              </li>
            ))}
          </ul>
        </Panel>
      ))}
    </div>
  );
}

function EffectivenessTab() {
  const q = useQuery({ queryKey: ["portfolio-effectiveness"], queryFn: dashApi.portfolioEffectiveness });
  if (q.isLoading) return <LoadingState />;
  if (q.isError) return <ErrorState error={q.error} />;
  const e = q.data as EffectivenessResult;
  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3 text-[11px] text-slate-500">
        {e.no_causation} {e.no_ranking}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Stat label="Mature comparable outcomes" value={e.eligible} sub={`min sample ${e.min_sample ?? 5}`} />
        <span className={`rounded px-2 py-1 text-xs ${e.sample_status === "adequate" ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`}>
          {e.sample_status ?? "n/a"}
        </span>
      </div>
      {e.sample_status !== "adequate" ? (
        <Panel title="Aggregate rates withheld"><EmptyState>{e.note ?? "Insufficient sample for aggregate rates — counts only."}</EmptyState>
          {e.label_counts && (
            <ul className="mt-2 space-y-0.5 text-xs">
              {Object.entries(e.label_counts).map(([k, v]) => <li key={k} className="text-slate-400">{k}: <span className="text-slate-200">{v}</span></li>)}
            </ul>
          )}
        </Panel>
      ) : (
        <>
          <Panel title="Alignment distribution" subtitle="Directional counts — never a score.">
            <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-3">
              {Object.entries(e.distribution ?? {}).map(([k, v]) => (
                <div key={k} className="rounded border border-slate-800 bg-slate-900/40 px-2 py-1.5">
                  <div className="text-lg text-slate-200">{v}</div><div className="text-[10px] text-slate-500">{k}</div>
                </div>
              ))}
            </div>
          </Panel>
          <GroupTable title="By decision category" rows={e.groups?.by_category} />
          <GroupTable title="By scenario type" rows={e.groups?.by_scenario_type} />
          <GroupTable title="By scope status" rows={e.groups?.by_scope_status} />
        </>
      )}
    </div>
  );
}

function GroupTable({ title, rows }: { title: string; rows?: Record<string, { count: number; sample_status: string; broadly_aligned_pct?: number }> }) {
  if (!rows || Object.keys(rows).length === 0) return null;
  return (
    <Panel title={title}>
      <table className="w-full text-xs">
        <thead><tr className="text-left text-[10px] uppercase text-slate-500"><th className="pb-1 pr-3">Group</th><th className="pb-1 pr-3">Count</th><th className="pb-1 pr-3">Sample</th><th className="pb-1">Broadly aligned</th></tr></thead>
        <tbody>
          {Object.entries(rows).map(([k, r]) => (
            <tr key={k} className="border-t border-slate-800">
              <td className="py-1 pr-3 text-slate-200">{k}</td>
              <td className="py-1 pr-3 text-slate-400">{r.count}</td>
              <td className="py-1 pr-3 text-slate-500">{r.sample_status}</td>
              <td className="py-1 text-slate-400">{r.broadly_aligned_pct != null ? `${r.broadly_aligned_pct}%` : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Panel>
  );
}

const CADENCE_TYPES = ["weekly_executive", "monthly_management", "monthly_board"];

function CadencesTab() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["cadences"], queryFn: dashApi.cadences });
  const [title, setTitle] = useState("");
  const [ctype, setCtype] = useState("weekly_executive");
  const create = useMutation({
    mutationFn: () => dashApi.createCadence({ title, cadence_type: ctype }),
    onSuccess: () => { setTitle(""); qc.invalidateQueries({ queryKey: ["cadences"] }); },
  });
  return (
    <div className="space-y-5">
      <Panel title="Create a review cadence" subtitle="An IRONDash schedule + agenda filter set. Generating an agenda never changes a decision or action.">
        <div className="flex flex-wrap items-end gap-2">
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Cadence title"
            className="flex-1 rounded border border-slate-700 bg-slate-800 px-2 py-1.5 text-sm text-slate-200" />
          <select value={ctype} onChange={(e) => setCtype(e.target.value)} className="rounded border border-slate-700 bg-slate-800 px-2 py-1.5 text-sm text-slate-200">
            {CADENCE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <button onClick={() => create.mutate()} disabled={create.isPending || !title.trim()} className="rounded bg-sky-500/20 px-3 py-1.5 text-sm text-sky-200 disabled:opacity-40">Create</button>
        </div>
      </Panel>
      {q.isLoading ? <LoadingState /> : q.isError ? <ErrorState error={q.error} /> : (
        (q.data?.cadences.length ?? 0) === 0 ? <EmptyState>No cadences yet.</EmptyState>
          : <div className="space-y-2">{q.data!.cadences.map((c) => <CadenceRow key={c.id} c={c} />)}</div>
      )}
    </div>
  );
}

function CadenceRow({ c }: { c: Cadence }) {
  const qc = useQueryClient();
  const [preview, setPreview] = useState<AgendaResult | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const inv = () => qc.invalidateQueries({ queryKey: ["cadences"] });
  const toggle = useMutation({ mutationFn: () => dashApi.updateCadence(c.id, { enabled: !c.enabled }), onSuccess: inv });
  const doPreview = useMutation({ mutationFn: () => dashApi.cadencePreview(c.id), onSuccess: (d) => setPreview(d) });
  const run = useMutation({
    mutationFn: () => dashApi.cadenceRun(c.id),
    onSuccess: (d) => { setMsg(d.duplicate ? `Already generated for ${d.period_key} (no duplicate).` : `Generated for ${d.period_key}.`); inv(); },
  });
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/30 p-3 text-xs">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`rounded px-1.5 py-0.5 text-[10px] ${c.enabled ? "bg-emerald-500/15 text-emerald-300" : "bg-slate-800 text-slate-500"}`}>{c.enabled ? "enabled" : "disabled"}</span>
        <span className="text-slate-200">{c.title}</span>
        <span className="text-slate-500">{c.cadence_type}</span>
        {c.last_generated_at && <span className="text-slate-600">last: {c.last_generated_at.slice(0, 10)}</span>}
        <div className="ml-auto flex gap-2">
          <button onClick={() => toggle.mutate()} className="rounded bg-slate-700 px-2 py-0.5 text-slate-300">{c.enabled ? "disable" : "enable"}</button>
          <button onClick={() => doPreview.mutate()} className="rounded bg-slate-700 px-2 py-0.5 text-slate-300">preview agenda</button>
          <button onClick={() => run.mutate()} className="rounded bg-sky-500/15 px-2 py-0.5 text-sky-200">mark generated</button>
        </div>
      </div>
      {msg && <p className="mt-1 text-emerald-300">{msg}</p>}
      {preview && (
        <div className="mt-2 border-t border-slate-800 pt-2">
          <p className="text-slate-500">Preview — {preview.item_count} items across {preview.section_count} sections (records nothing).</p>
          <ul className="mt-1 space-y-0.5">
            {preview.sections.filter((s) => !s.empty).map((s) => (
              <li key={s.key} className="text-slate-400">{s.title}: <span className="text-slate-200">{s.items.length}</span></li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
